import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useCssStyle } from '../../customHook/getCssStyle';
import CustomPressable from '../../components/customPressable/customPressable';
import { ImageComp } from '../../components/Image/Image';
import { CROSS, MINUS, PLUS } from '../../assets';
import CustomCheckBoxWithProps from '../../components/checkBox/customCheckBox';
import { CustomModal } from '../../components/customModal';
import RenderHTML from 'react-native-render-html';
import { getCleanUrl } from '../../utils/Helper';

/**
 * ItemAddOnsModal — bottom-sheet modal for items with direct (flat) add-ons.
 * Shows item details, optional flat add-ons (checkboxes), cooking instructions,
 * and an "Add to Order" button.
 */
const ItemAddOnsModal = (props) => {
  const {
    selectedStoreItem = {},
    addonsList = {},
    configs = {},
    handleAddToCart,
    handleClose,
  } = props || {};

  const getStyles = useCssStyle();
  const { width, height } = useWindowDimensions();

  const { currencyDetails } = useSelector((state) => state.deviceConfigSlice);
  const currencyCode = currencyDetails?.code || '';

  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [cookingInstructions, setCookingInstructions] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showFullDesc, setShowFullDesc] = useState(false);

  // Resolve flat addons from addOnItem IDs
  const itemAddons = useMemo(() => {
    return (selectedStoreItem.addOnItem || [])
      .map(id => addonsList[id] || addonsList[String(id)])
      .filter(Boolean)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [selectedStoreItem.addOnItem, addonsList]);

  const toggleAddOn = (addOnInfo) => {
    setSelectedAddOns(prev => {
      if (prev.some(a => a.id === addOnInfo.id)) {
        return prev.filter(a => a.id !== addOnInfo.id);
      }
      return [...prev, addOnInfo].sort((a, b) => a.id - b.id);
    });
  };

  const totalPrice = useMemo(() => {
    const addonTotal = selectedAddOns.reduce((s, a) => s + parseFloat(a.price || 0), 0);
    return ((parseFloat(selectedStoreItem.itemPrice || 0) + addonTotal) * quantity).toFixed(2);
  }, [selectedAddOns, selectedStoreItem.itemPrice, quantity]);

  const handleAddItem = () => {
    handleAddToCart(
      { ...selectedStoreItem, quantity, ...(cookingInstructions && { cookingInstructions }) },
      selectedAddOns,
      true,
    );
    handleClose();
  };

  const descriptionStyle = useMemo(() => {
    const s = getStyles(['list_itemContainer_textContainer_subtitle']);
    return Array.isArray(s) ? Object.assign({}, ...s) : s || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemImage = selectedStoreItem.storeItemImages?.find(i => i.imageType === 'LARGE')?.imagePath
    || selectedStoreItem.storeItemImages?.[0]?.imagePath;
  const cleanImageUrl = itemImage ? getCleanUrl(itemImage) : null;

  return (
    <CustomModal onRequestClose={handleClose}>
      {/* Close button above the sheet, centered on backdrop */}
      <View style={getStyles(['hcommerce_modal_closeAboveContainer'])}>
        <CustomPressable onPress={handleClose} style={getStyles(['hcommerce_modal_closeAboveBtn'])}>
          <ImageComp source={CROSS} style={getStyles(['hcommerce_modal_closeAboveIcon'])} />
        </CustomPressable>
      </View>

      <View style={[getStyles(['hcommerce_modal_sheet']), { maxHeight: height * 0.92 }]}>
        {/* Drag indicator */}
        <View style={getStyles(['hcommerce_modal_dragIndicatorWrap'])}>
          <View style={getStyles(['hcommerce_modal_dragIndicator'])} />
        </View>

        {/* Scrollable content */}
        <ScrollView
          style={getStyles(['hcommerce_modal_scroll'])}
          contentContainerStyle={getStyles(['hcommerce_modal_scrollContentFlat'])}
          showsVerticalScrollIndicator={false}
        >
          {/* Item image */}
          {cleanImageUrl && (
            <View style={getStyles(['hcommerce_modal_imageContainer'])}>
              <ImageComp source={cleanImageUrl} style={getStyles(['hcommerce_modal_image'])} resizeMode="cover" />
            </View>
          )}

          {/* Item details — centered */}
          <View style={getStyles(['hcommerce_modal_itemInfoFlat'])}>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_title']), getStyles(['hcommerce_modal_itemName'])]}>
              {selectedStoreItem.itemName}
            </Text>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_subtitle']), getStyles(['hcommerce_modal_itemPrice'])]}>
              {currencyCode}{selectedStoreItem.itemPrice}
            </Text>
            {selectedStoreItem.itemDescription ? (
              <>
                <View style={showFullDesc ? null : getStyles(['hcommerce_modal_descClamp'])}>
                  <RenderHTML
                    contentWidth={width - 48}
                    source={{ html: selectedStoreItem.itemDescription }}
                    baseStyle={{ ...descriptionStyle, textAlign: 'center' }}
                  />
                </View>
                <TouchableOpacity onPress={() => setShowFullDesc(v => !v)} activeOpacity={0.7}>
                  <Text style={getStyles(['hcommerce_modal_showMoreBtn'])}>
                    {showFullDesc ? 'Show less' : 'Show more'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}
          </View>

          {/* Flat add-ons */}
          {itemAddons.length > 0 && (
            <View style={getStyles(['hcommerce_flat_groupSection'])}>
              <View style={getStyles(['hcommerce_flat_groupHeader'])}>
                <Text style={getStyles(['AddOnGrouping_drawer_header_title'])}>Add-ons</Text>
                <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])}>Optional</Text>
              </View>
              {itemAddons.map(addon => {
                const isSelected = selectedAddOns.some(a => a.id === addon.id);
                const hasPrice = parseFloat(addon.price || 0) > 0;
                return (
                  <TouchableOpacity
                    key={addon.id}
                    style={getStyles(['hcommerce_flat_addonRow'])}
                    onPress={() => toggleAddOn(addon)}
                    activeOpacity={0.8}
                  >
                    <View style={getStyles(['hcommerce_flat_addonRowLeft'])}>
                      {addon.largeImage && (
                        <View style={getStyles(['hcommerce_flat_addonImage'])}>
                          <ImageComp
                            source={getCleanUrl(addon.largeImage)}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </View>
                      )}
                      <View style={getStyles(['hcommerce_flat_addonText'])}>
                        <Text style={getStyles(['list_itemContainer_textContainer_title'])} numberOfLines={2}>
                          {addon.name}
                        </Text>
                        {addon.description ? (
                          <Text style={getStyles(['list_itemContainer_textContainer_subtitle'])} numberOfLines={2}>
                            {addon.description}
                          </Text>
                        ) : null}
                        {hasPrice && (
                          <Text style={getStyles(['AddOnsModalContent_addons_itemContainer_textContainer_price'])}>
                            +{currencyCode}{addon.price}
                          </Text>
                        )}
                      </View>
                    </View>
                    <CustomCheckBoxWithProps
                      value={isSelected}
                      onValueChange={() => toggleAddOn(addon)}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {/* Cooking instructions */}
          <View style={getStyles(['hcommerce_flat_groupSection'])}>
            <Text style={getStyles(['AddOnGrouping_drawer_header_title'])}>Special Instructions</Text>
            <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])}>Optional</Text>
            <TextInput
              style={getStyles(['hcommerce_modal_instructionsInput'])}
              placeholder="e.g. No onions, extra sauce..."
              placeholderTextColor="rgba(0,0,0,0.4)"
              value={cookingInstructions}
              onChangeText={setCookingInstructions}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={getStyles(['hcommerce_modal_bottomPad'])} />
        </ScrollView>

        {/* Footer */}
        <SafeAreaView edges={['bottom']}>
          <View style={getStyles(['hcommerce_modal_footerFlat'])}>
            {!configs.disableAddRemoveItemsButtons && (
              <View style={getStyles(['hcommerce_modal_footerRowFlat'])}>
                <View style={getStyles(['hcommerce_modal_qtyBlockFlat'])}>
                  <CustomPressable
                    style={getStyles(['hcommerce_modal_qtyBtnFlat'])}
                    onPress={() => setQuantity(q => Math.max(1, q - 1))}
                  >
                    <ImageComp source={MINUS} style={getStyles(['hcommerce_modal_qtyIconFlat'])} />
                  </CustomPressable>
                  <Text style={getStyles(['additem_controls_qty_text'])}>{quantity}</Text>
                  <CustomPressable
                    style={getStyles(['hcommerce_modal_qtyBtnFlat'])}
                    onPress={() => setQuantity(q => q + 1)}
                  >
                    <ImageComp source={PLUS} style={getStyles(['hcommerce_modal_qtyIconFlat'])} />
                  </CustomPressable>
                </View>
                <CustomPressable style={[getStyles(['primaryBtn']), getStyles(['hcommerce_modal_addBtnFlex'])]} onPress={handleAddItem} id="primaryBtn">
                  <Text style={getStyles(['primaryBtnTxt'])}>
                    Add to Order · {currencyCode}{totalPrice}
                  </Text>
                </CustomPressable>
              </View>
            )}
          </View>
        </SafeAreaView>
      </View>
    </CustomModal>
  );
};

export default ItemAddOnsModal;
